import { useStore } from "zustand"
import { createStore } from "zustand/vanilla"

import type { CustomThemeId } from "@/styles/themes/custom-themes"

import {
  getThemeSeedForBuilder,
  type ThemeId,
  type ThemeSeed,
} from "@/styles/themes"

export type ColorSection = "base" | "primary" | "accent"

type ThemeBuilderPageState = {
  editingThemeId: CustomThemeId | null
  activeSection: ColorSection
  draft: ThemeSeed
  shareCode: string
  importCode: string
  importLabel: string
  shareMessage: string | null
  importError: string | null
  isSaving: boolean
  saveError: string | null

  setEditingThemeId: (themeId: CustomThemeId | null) => void
  setActiveSection: (section: ColorSection) => void
  setDraft: (updater: ThemeSeed | ((current: ThemeSeed) => ThemeSeed)) => void
  setShareCode: (value: string) => void
  setImportCode: (value: string) => void
  setImportLabel: (value: string) => void
  setShareMessage: (value: string | null) => void
  setImportError: (value: string | null) => void
  setIsSaving: (value: boolean) => void
  setSaveError: (value: string | null) => void

  hydrateDraft: (themeId: ThemeId) => void
  resetDraft: (themeId: ThemeId) => void
}

function isCustomId(themeId: ThemeId): themeId is CustomThemeId {
  return themeId.startsWith("custom:")
}

export const createThemeBuilderPageStore = (initialThemeId: ThemeId) =>
  createStore<ThemeBuilderPageState>((set) => ({
    editingThemeId: isCustomId(initialThemeId) ? initialThemeId : null,
    activeSection: "primary",
    draft: getThemeSeedForBuilder(initialThemeId),
    shareCode: "",
    importCode: "",
    importLabel: "Imported Theme",
    shareMessage: null,
    importError: null,
    isSaving: false,
    saveError: null,

    setEditingThemeId: (themeId) => set({ editingThemeId: themeId }),
    setActiveSection: (section) => set({ activeSection: section }),
    setDraft: (updater) =>
      set((state) => ({
        draft:
          typeof updater === "function"
            ? (updater as (current: ThemeSeed) => ThemeSeed)(state.draft)
            : updater,
      })),
    setShareCode: (value) => set({ shareCode: value }),
    setImportCode: (value) => set({ importCode: value }),
    setImportLabel: (value) => set({ importLabel: value }),
    setShareMessage: (value) => set({ shareMessage: value }),
    setImportError: (value) => set({ importError: value }),
    setIsSaving: (value) => set({ isSaving: value }),
    setSaveError: (value) => set({ saveError: value }),

    hydrateDraft: (themeId) =>
      set({
        draft: getThemeSeedForBuilder(themeId),
        editingThemeId: isCustomId(themeId) ? themeId : null,
      }),
    resetDraft: (themeId) =>
      set({
        draft: getThemeSeedForBuilder(themeId),
        editingThemeId: isCustomId(themeId) ? themeId : null,
      }),
  }))

export type ThemeBuilderPageStore = ReturnType<
  typeof createThemeBuilderPageStore
>

export const useThemeBuilderState = <T>(
  store: ThemeBuilderPageStore,
  selector: (state: ThemeBuilderPageState) => T,
) => useStore(store, selector)
